import { Box, Grid } from '@mui/material';
import React, { FC, useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { SliderProvider } from '@/data/context/content';

type Props = {
	slides?: JSX.Element[];
	children?: JSX.Element[];
	id?: string;
	withoutmargin?: boolean;
};

const defaultCarouselProps = {
	dots: false,
	infinite: true,
	slidesToShow: 1,
	slidesToScroll: 1,
	autoplay: true,
	autoplaySpeed: 8000,
	cssEase: 'linear',
	swipeToSlide: true,
	focusOnSelect: true,
};

export const CarouselSlider: FC<Props> = ({ id, slides, withoutmargin = false, children }) => {
	const _slides = useMemo(() => {
		if (slides) return slides;
		const cleanChildren = React.Children.toArray(children).filter((child) =>
			React.isValidElement(child)
		);
		return cleanChildren;
	}, [slides, children]);

	const [activeSlide, setActiveSlide] = useState(0);
	const sliderRef = useRef<Slider>(null);
	const [mouseMoved, setMouseMoved] = useState(false);

	if (_slides.length === 1) {
		return (
			<SliderProvider value={{ mouseMoved }}>
				<Box
					id={id}
					width="100vw"
					minWidth="100%"
					maxWidth="100%"
					marginBottom={withoutmargin ? '' : { xs: '20px', sm: '40px' }}
				>
					{_slides}
				</Box>
			</SliderProvider>
		);
	}

	return (
		<Box
			id={id}
			width="100vw"
			minWidth="100%"
			maxWidth="100%"
			position="relative"
			marginBottom={withoutmargin ? '' : { xs: '20px', sm: '40px' }}
			sx={{ overflowX: 'hidden' }}
		>
			<SliderProvider value={{ mouseMoved }}>
				<Slider
					{...defaultCarouselProps}
					ref={sliderRef}
					beforeChange={(_, next) => {
						setActiveSlide(next);
						setMouseMoved(true);
					}}
					afterChange={() => {
						setMouseMoved(false);
					}}
				>
					{_slides.map((slide, index) => (
						<Grid key={`id_carousel_${index}`}>{slide}</Grid>
					))}
				</Slider>
			</SliderProvider>
			{_slides.length > 1 ? (
				<Box
					width="100vw"
					minWidth="100vw"
					maxWidth="100vw"
					position={'absolute'}
					justifyContent={'center'}
					display={'inline-flex'}
					bottom={'20px'}
					zIndex={'20'}
				>
					<Box
						sx={{
							display: 'flex',
							direction: 'row',
							gap: '0px !important',
							borderRadius: '10px',
							backgroundColor: '#FFFFFF80',
						}}
					>
						{Array.from({ length: _slides.length }).map((_, index) => (
							<Box
								key={`id_carousel_dot_${index}`}
								sx={{
									backgroundColor: 'white',
									width: {
										xs: `calc(150px/${_slides.length})`,
										md: `calc(300px/${_slides.length})`,
									},
									height: '6px',
									margin: '0px !important',
									padding: '0px !important',
									opacity: activeSlide === index ? '1 !important' : '0.5',
									borderTopLeftRadius: index === 0 ? '10px' : '0px',
									borderBottomLeftRadius: index === 0 ? '10px' : '0px',
									borderTopRightRadius: index === _slides.length - 1 ? '10px' : '0px',
									borderBottomRightRadius: index === _slides.length - 1 ? '10px' : '0px',
									cursor: 'pointer',
								}}
								onClick={() => {
									sliderRef.current?.slickGoTo(index);
								}}
							/>
						))}
					</Box>
				</Box>
			) : null}
		</Box>
	);
};
